import { useEffect, useRef, useState } from 'react';
import { logger } from 'src/services';
import { Button } from '@insight-health-ai/roseate/components';
import AmbientAnimation from 'src/modules/ambient_solo/components/CurrentEncounter/AmbientAnimation';
import AmbientAnimationPaused from 'src/modules/ambient_solo/components/CurrentEncounter/AmbientAnimationPaused';
import { useParams } from 'react-router-dom';
import { finalizeEHRSession } from 'src/modules/SuperAdmin/store/actions/ehrcredentials.action';
import { useDispatch } from 'react-redux';

class VisitRecorder {
  constructor(sessionId) {
    this.isRecording = false;
    this.sessionId = sessionId;
  }

  initialize = async () => {
    logger.warn('initialize EpicHaiku');
    window.visitRecorder = this;
    try {
      // Initialize communication with Haiku
      await this.sendMessage('appInitialization', {
        version: '2.0',
      });

      // Send appInitialized message and get session state
      const sessionState = await this.sendMessage('appInitialized', {});
      logger.warn({ sessionState });
      this.recordingVersion = sessionState.recordingVersion;
      this.isFinalized = sessionState.finalized;
    } catch (error) {
      logger.error('EPIC Haiku: Initialization failed:', { error });
    }
  };

  sendMessage = async (messageName, data) => {
    logger.warn({ sendMessage: { messageName, data } });
    return window.webkit.messageHandlers.epicMobileVisitRecorder.postMessage({
      name: `com.epic.visitRecorderLink.${messageName}`,
      sessionID: this.sessionId,
      data: JSON.stringify(data),
    });
  };

  canBecomeInactive = () => {
    logger.warn('canBecomeInactive', this.isRecording);
    return !this.isRecording;
  };

  willResign = () => {
    logger.warn('willResign');
    this.isRecording = false;
    window.visitRecorder = undefined;
  };

  willBackground = () => {
    // Handle backgrounding state
    logger.warn('willBackground');
  };

  returnedToForeground = () => {
    logger.warn('returnedToForeground');
    // Handle return to foreground
  };

  willBecomeActive = async () => {
    const hasMicrophoneAccess = await this.sendMessage('canRecord', {});
    logger.warn({ willBecomeActive: { hasMicrophoneAccess } });
    if (!hasMicrophoneAccess) {
      window.visitRecorder = undefined;
    }
  };

  resumeRecording = async () => {
    logger.warn({ resumeRecording: { isFinalized: this.isFinalized } });
    if (!this.isFinalized) {
      this.isRecording = true;
      await this.sendMessage('recordingStatus', { active: true });
    }
  };

  startRecording = async () => {
    logger.warn({ startRecording: { isFinalized: this.isFinalized } });
    if (!this.isFinalized) {
      this.isRecording = true;
      const recordingNumber = await this.sendMessage('recordingStatus', {
        active: true,
      });
      logger.warn({ startRecording: { recordingNumber } });
    }
  };

  stopRecording = async () => {
    logger.warn({ stopRecording: { isRecording: this.isRecording } });
    if (this.isRecording) {
      this.isRecording = false;
      await this.sendMessage('recordingStatus', { active: false });
    }
  };

  finalizeSession = async () => {
    logger.warn({ finalizeSession: { isFinalized: this.isFinalized } });
    const success = await this.sendMessage('finalizeSession', {});
    logger.warn({ finalizeSessionResponse: { success } });
    if (success) {
      this.isFinalized = true;
    }
    return success;
  };
}

export const EPICLaunch = () => {
  const { sessionId } = useParams();
  const dispatch = useDispatch();
  const recorderRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);

  // Initialize VisitRecorder when component mounts
  useEffect(() => {
    logger.warn('EPICLaunch');
    if (!recorderRef.current) {
      const recorder = new VisitRecorder(sessionId);
      const init = async () => {
        await recorder.initialize();
        setIsFinalized(recorder.isFinalized);
        if (recorder.isFinalized) {
          setSessionStarted(true);
        }
      };
      init();
      recorderRef.current = recorder;
    }
  }, [sessionId]);

  const handleStart = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    await recorder.startRecording();
    setIsRecording(true);
    setSessionStarted(true);
  };

  const handlePause = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    await recorder.stopRecording();
    setIsRecording(false);
  };

  const handleResume = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    await recorder.resumeRecording();
    setIsRecording(true);
  };

  const handleFinalize = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    if (isRecording) {
      await recorder.stopRecording();
      setIsRecording(false);
    }
    const success = await recorder.finalizeSession();
    if (success) {
      setIsFinalized(true);
      dispatch(finalizeEHRSession(sessionId));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-white">
      {isFinalized ? (
        <p className="text-lg font-medium text-gray-700">
          Note has been generated.
        </p>
      ) : !sessionStarted ? (
        <>
          <div className="flex items-center justify-center">
            <AmbientAnimationPaused />
          </div>
          <div className="w-full max-w-xs flex flex-col space-y-3 p-8">
            <Button onClick={handleStart}>Start visit</Button>
          </div>
        </>
      ) : isRecording ? (
        <>
          <div className="flex items-center justify-center">
            <AmbientAnimation />
          </div>
          <div className="w-full max-w-xs flex flex-col space-y-3 p-8">
            <Button appearance="secondary" onClick={handlePause}>
              Pause visit
            </Button>
            <Button appearance="primary" onClick={handleFinalize}>
              Generate note
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-center">
            <AmbientAnimationPaused />
          </div>
          <div className="w-full max-w-xs flex flex-col space-y-3 p-8">
            <Button appearance="secondary" onClick={handleResume}>
              Resume visit
            </Button>
            <Button appearance="primary" onClick={handleFinalize}>
              Generate note
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EPICLaunch;
